import React, { ReactElement, useState, useMemo } from "react";
import { TileLayout } from "@progress/kendo-react-layout";
import { useWindowSize } from 'usehooks-ts';
import { Groups, Endpoints, GreasePump } from "../../../../Models/Assets/AssetTemplateReadDto";
import AssetImageComponent from "../../../Assets/Components/Images/AssetImageComponent";
import GreasePumpsTableTemplateComponent from "../Tables/GreasePumpsTableTemplateComponent";

interface Props {
  formRenderProps?: any;
  groups: Groups[];
  endpoints: Endpoints[];
  greasePumps: GreasePump[];
  setGreasePumps: React.Dispatch<React.SetStateAction<GreasePump[]>>;
  imageBase64: string | null;
  isEditMode?: boolean;
}

export default function AssetTemplateGreasePumpComponent(props: Props): ReactElement {
  const { height, width } = useWindowSize();
  const containerHeight = height - 300;
  const isSingleColumn = width < 768;
  const rowHeight = 25; // Fixed like AssetConfigurationComponent for proper single-column stacking

  const { groups, endpoints, greasePumps, setGreasePumps, formRenderProps, imageBase64, isEditMode } = props;

  const [selectedGreasePumpId, setSelectedGreasePumpId] = useState<string | null>(() => {
    const active = greasePumps.filter((p: GreasePump) => p.status === 'Active' || (p.status === undefined && p.isEnabled !== false));
    return active.length > 0 ? active[0].id : null;
  });

  // Use image from parent state (Dialog) - this persists across steps
  const imageUrl = useMemo(() => {
    if (!imageBase64 || typeof imageBase64 !== 'string' || imageBase64.length === 0) {
      return null;
    }
    if (!imageBase64.startsWith('data:image')) {
      let mime = 'image/png';
      if (imageBase64.startsWith('/9j/')) mime = 'image/jpeg';
      else {
        try {
          const decoded = atob(imageBase64.slice(0, 80));
          if (decoded.trimStart().startsWith('<svg') || decoded.trimStart().startsWith('<?xml')) {
            mime = 'image/svg+xml';
          }
        } catch {
          /* ignore */
        }
      }
      return `data:${mime};base64,${imageBase64}`;
    }
    return imageBase64;
  }, [imageBase64]);

  const templateName = formRenderProps?.valueGetter('name') || '';

  const handleGreasePumpSelect = (pumpId: string | null) => {
    setSelectedGreasePumpId(pumpId);
  };

  const handleGreasePumpTableSelectionChange = (pumpId: string | null) => {
    setSelectedGreasePumpId(pumpId);
  };

  const handleMarkersChange = (markers: any[]) => {
    const updatedGreasePumps = greasePumps.map(pump => {
      const marker = markers.find(m => m.id === pump.id);
      if (marker) {
        return { ...pump, xPos: marker.position.xPos, yPos: marker.position.yPos };
      }
      return pump;
    });
    setGreasePumps(updatedGreasePumps);
  };

  const handleGreasePumpsTableChange = (updatedGreasePumps: GreasePump[]) => {
    const mergedGreasePumps = updatedGreasePumps.map(updatedPump => {
      const existingPump = greasePumps.find(g => g.id === updatedPump.id);
      return {
        ...updatedPump,
        xPos: existingPump?.xPos || updatedPump.xPos || 0,
        yPos: existingPump?.yPos || updatedPump.yPos || 0
      };
    });
    setGreasePumps(mergedGreasePumps);
  };

  const getUniqueGreasePumpName = (baseName: string, pumps: GreasePump[]): string => {
    const active = pumps.filter((p) => p.status === 'Active' || (p.status === undefined && p.isEnabled !== false));
    const used = new Set(active.map((p) => (p.name || '').trim().toLowerCase()));
    let candidate = baseName.trim();
    if (!used.has(candidate.toLowerCase())) return candidate;
    for (let n = 2; n < 1000; n++) {
      candidate = `${baseName} (${n})`;
      if (!used.has(candidate.toLowerCase())) return candidate;
    }
    return `${baseName} (${Date.now()})`;
  };

  const handleAddGreasePump = () => {
    const getNextPumpId = (): string => {
      const activePumps = greasePumps.filter(
        (p: GreasePump) => p.status === 'Active' || (p.status === undefined && p.isEnabled !== false)
      );
      if (activePumps.length === 0) return '1';
      const numericIds = activePumps
        .map(p => parseInt(p.id, 10))
        .filter(n => !Number.isNaN(n) && n >= 1);
      const next = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
      return String(next);
    };
    const newId = getNextPumpId();
    const nextPumpNumber = parseInt(newId, 10);
    const baseName = `Grease Pump ${nextPumpNumber}`;
    const uniqueName = getUniqueGreasePumpName(baseName, greasePumps);
    const newGreasePump: GreasePump = {
      id: newId,
      name: uniqueName,
      deviceId: '',
      serialNo: '',
      channelNo: 0,
      xPos: 50,
      yPos: 50,
      status: 'Active',
      devices: []
    };
    setGreasePumps([...greasePumps, newGreasePump]);
    setSelectedGreasePumpId(newId);
  };

  const tileData = [
    {
      body: (
        <div className="h-full w-full flex flex-col">
          {imageUrl ? (
            <AssetImageComponent
              assetData={{
                id: '', name: templateName || '', description: '', imageId: '',
                isGreasePumpConnectivityEnabled: true, endpoints: endpoints,
                gateways: [], groups: groups,
              }}
              selectedGroupId={selectedGreasePumpId}
              onMarkerSelect={handleGreasePumpSelect}
              assetImageUrl={imageUrl}
              onMarkersChange={handleMarkersChange}
              templateName={templateName || 'Template image'}
              mode="greasePump"
              greasePumps={greasePumps}
              selectedGreasePumpId={selectedGreasePumpId}
              onGreasePumpSelect={handleGreasePumpSelect}
              onGreasePumpMarkersChange={handleMarkersChange}
              alwaysEditMode={true}
              instructionText="Drag grease pumps to position them on the image"
              hideDisabledOrDeleted={true}
            />
          ) : (
            <div className="flex justify-center items-center h-full text-gray-500">
              No image available. Please upload an image in the Details step.
            </div>
          )}
        </div>
      ),
      reorderable: false,
    },
    {
      body: (
        <div className="h-full flex flex-col overflow-hidden">
          <GreasePumpsTableTemplateComponent
            greasePumps={greasePumps}
            onGreasePumpsChange={handleGreasePumpsTableChange}
            onSelectionChange={handleGreasePumpTableSelectionChange}
            selectedGreasePumpId={selectedGreasePumpId}
            onAddGreasePump={handleAddGreasePump}
            isEditTemplate={isEditMode}
            instructionText="Add or edit grease pumps. Position them on the image."
          />
        </div>
      ),
      reorderable: false,
    },
  ];

  /** Same approach as AssetConfigurationComponent: use rowSpan for single-column stacking; smaller tiles in single column */
  const columnsDisplayPosition = isSingleColumn
    ? [
        { col: 1, colSpan: 1, rowSpan: 30 },
        { col: 1, colSpan: 1, rowSpan: 30 },
      ]
    : [
        { col: 1, colSpan: 1, rowSpan: 50 },  // image on left
        { col: 2, colSpan: 1, rowSpan: 50 },  // table on right
      ];

  return (
    <div className="w-full pt-4 overflow-y-auto overscroll-contain" style={{ height: containerHeight }}>
      <TileLayout
        columns={isSingleColumn ? 1 : 2}
        rowHeight={rowHeight}
        positions={columnsDisplayPosition}
        gap={{ rows: 10, columns: 10 }}
        items={tileData}
        className="w-full dashboard-page-tile"
        style={{ width: '100%', height: isSingleColumn ? 'auto' : '100%' }}
      />
    </div>
  );
}
